import {Box, Stack} from '@mui/material';
import {forwardRef, ReactNode, useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {allowScrollY, preventScroll} from '../../common';

export const ScrollableStack = forwardRef<HTMLDivElement, {padding?: string; gap?: string; showScrollBar?: boolean; children: ReactNode}>((props, ref) => {
	const {padding, gap, showScrollBar, children} = props;

	const [isLocked, setIsLocked] = useState(false);
	const location = useLocation();
	const scrollSx = showScrollBar ? allowScrollY : {...allowScrollY, '&::-webkit-scrollbar': {display: 'none'}};

	const containerCurrent = ref && typeof ref === 'object' ? ref.current : null;

	const scrollToTop = useCallback(() => {
		if (containerCurrent) containerCurrent.scrollTop = 0;
	}, [containerCurrent]);

	useEffect(() => {
		const checkOverflowAndScroll = () => {
			if (containerCurrent) {
				const {scrollHeight, clientHeight} = containerCurrent;
				setIsLocked(scrollHeight - 1 <= clientHeight);
				scrollToTop();
			} else setIsLocked(false);
		};

		const attemptScroll = (attempts = 0) => {
			if (attempts < 5) {
				setTimeout(() => {
					checkOverflowAndScroll();
					if (containerCurrent && containerCurrent.scrollTop !== 0) attemptScroll(attempts + 1);
				}, 200 * (attempts + 1));
			}
		};

		attemptScroll();

		const handleResize = () => checkOverflowAndScroll();

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [children, containerCurrent, location, scrollToTop]);

	// 100% + 1px is a hack to prevent the overflowBehavior from appearing when the content is not overflowing on A2HS
	return (
		<Box id="stack-wrapper" position="relative" ref={ref} width="100%" height="100%" sx={!isLocked ? scrollSx : preventScroll}>
			<Stack width="100%" minHeight={`calc(100% + 1px)`} overflow="visible" gap={gap} padding={padding} alignItems="center" justifyContent="space-between">
				{children}
			</Stack>
		</Box>
	);
});
